/**
 * Card order for the evals list — most recently updated group first.
 *
 * "Updated" reads two clocks, because neither one alone covers a group.
 * `updated_at` is a definition upsert (a push to /v1/eval-groups, or a repo
 * sync), so a group that scored runs yesterday can still carry a month-old
 * stamp. `last_run_at` is null until the first scored run lands, so a group
 * whose tasks just synced has nothing to sort on. The newest of the two is
 * the last time anything happened to the group.
 *
 * Reference runs never move a group: `last_run_at` is computed from scored
 * runs only, so pushing a competitor baseline doesn't float a stale group
 * to the top.
 */

import type { EvalGroupSummary } from '@/api/generated/model'

/** Epoch ms of the most recent thing that happened to a group. */
export function groupActivityAt(group: EvalGroupSummary): number {
  const definedAt = Date.parse(group.updated_at)
  if (group.last_run_at === null) return definedAt
  return Math.max(definedAt, Date.parse(group.last_run_at))
}

/** Sort comparator putting the most recently updated group first. */
export function byRecentActivity(a: EvalGroupSummary, b: EvalGroupSummary): number {
  return groupActivityAt(b) - groupActivityAt(a)
}
